import type { Namespace } from '../types';

// SOC console (R-068/D-070 — DASH-SOC). DE drafted — queued for review.
export const soc = {
  title: { de: 'Service Operations Center', en: 'Service Operations Center' },
  desc: {
    de: 'Leitwarte: KPI-Kacheln mit Drilldown, Alarm-Ticker und Klimastatus aller Räume.',
    en: 'Control room: KPI tiles with drill-down, alarm ticker and the climate status of every room.',
  },

  // KPI tiles
  tileP1: { de: 'P1 offen', en: 'P1 open' },
  tileP2: { de: 'P2 offen', en: 'P2 open' },
  tileP3P4: { de: 'P3/P4 offen', en: 'P3/P4 open' },
  tileAlarms: { de: 'Alarme', en: 'Alarms' },
  // `in_band` from the cloud rollup means "a reading arrived in the last 5 min" —
  // it is not judged against a band, so the tile says what it knows.
  tileRoomsOk: { de: 'Räume melden', en: 'Rooms reporting' },
  tileChanges: { de: 'Changes', en: 'Changes' },

  // Drill-down
  drillIncidents: { de: 'Störungen ({scope})', en: 'Incidents ({scope})' },
  drillAlarms: { de: 'Alarm-Ticker', en: 'Alarm ticker' },
  drillChanges: { de: 'Changes', en: 'Changes' },
  detail: { de: 'Details', en: 'Details' },
  backToList: { de: 'Zurück zur Liste', en: 'Back to list' },
  openInMaintenance: { de: 'In Maintenance öffnen', en: 'Open in Maintenance' },
  // R-173: a deep link pointed at an alarm that no longer exists. Naming it beats
  // an empty panel that reads as "nothing selected" when something WAS asked for.
  alarmGone: {
    de: 'Dieser Alarm ist nicht mehr vorhanden — er wurde möglicherweise gelöscht.',
    en: 'This alarm no longer exists — it may have been deleted.',
  },
  listEmpty: { de: 'Keine Einträge.', en: 'No entries.' },
  listEmptyAlarms: { de: 'Keine Alarme für diesen Filter.', en: 'No alarms match this filter.' },
  listEmptyIncidents: {
    de: 'Keine offenen Störungen dieser Priorität.',
    en: 'No open incidents at this priority.',
  },
  listEmptyChanges: { de: 'Keine offenen Changes.', en: 'No open changes.' },
  listError: { de: 'Die Liste konnte nicht geladen werden.', en: 'Could not load this list.' },
  ariaFilterStatus: { de: 'Status', en: 'Status' },
  ariaFilterSeverity: { de: 'Schweregrad', en: 'Severity' },
  shown: { de: '{count} angezeigt', en: '{count} shown' },
  openDetails: { de: 'Details zu {name} öffnen', en: 'Open details for {name}' },
  filterAll: { de: 'Alle', en: 'All' },
  // On a phone the two filters are side-by-side dropdowns with no visible label,
  // so their "All" has to say which "all" it is.
  filterAllStatuses: { de: 'Alle Status', en: 'All statuses' },
  filterAllSeverities: { de: 'Alle Schweregrade', en: 'All severities' },
  loadMore: { de: 'Mehr laden', en: 'Load more' },

  // Alarms
  sevWarning: { de: 'Warnung', en: 'Warning' },
  sevCritical: { de: 'Kritisch', en: 'Critical' },
  stOpen: { de: 'Offen', en: 'Open' },
  stAcked: { de: 'Bestätigt', en: 'Acked' },
  stResolved: { de: 'Gelöst', en: 'Resolved' },
  cleared: { de: 'Abgeklungen', en: 'Cleared' },
  escalated: { de: 'Eskaliert', en: 'Escalated' },
  kindClimate: { de: 'Klima-Ausbruch', en: 'Climate breach' },
  kindFault: { de: 'Gerätefehler', en: 'Device fault' },
  kindOffline: { de: 'Gerät offline', en: 'Device offline' },
  kindAiDetection: { de: 'KI-Erkennung', en: 'AI detection' },
  kindColdChain: { de: 'Kühlkette', en: 'Cold chain' },
  kindSiteOffline: { de: 'Standort offline', en: 'Site offline' },
  kindServiceOffline: { de: 'Dienst offline', en: 'Service offline' },
  ack: { de: 'Bestätigen', en: 'Acknowledge' },
  resolve: { de: 'Lösen', en: 'Resolve' },
  resolveTitle: { de: 'Alarm lösen', en: 'Resolve alarm' },
  resolveHint: {
    de: 'Eine Lösungsnotiz ist Pflicht (SEN-AL-003).',
    en: 'A resolution note is mandatory (SEN-AL-003).',
  },
  note: { de: 'Lösungsnotiz', en: 'Resolution note' },
  toastAcked: { de: 'Alarm bestätigt.', en: 'Alarm acknowledged.' },
  toastResolved: { de: 'Alarm gelöst.', en: 'Alarm resolved.' },
  value: { de: 'Messwert', en: 'Value' },
  band: { de: 'Toleranzband', en: 'Tolerance band' },
  raisedAt: { de: 'Ausgelöst', en: 'Raised' },
  ackedAt: { de: 'Bestätigt am', en: 'Acknowledged at' },
  clearedAt: { de: 'Abgeklungen am', en: 'Cleared at' },
  linkedIncident: { de: 'Verknüpfte Störung', en: 'Linked incident' },
  alarmTitle: { de: 'Alarm', en: 'Alarm' },
  incidentTitle: { de: 'Störung', en: 'Incident' },
  changeTitle: { de: 'Change', en: 'Change' },
  readingTitle: { de: 'Messwert gegen Band', en: 'Reading against band' },
  noReading: { de: 'Dieser Alarm trägt keinen Messwert.', en: 'This alarm carries no reading.' },
  detailsTitle: { de: 'Details', en: 'Details' },
  lifecycleTitle: { de: 'Verlauf', en: 'Lifecycle' },
  lifeRaised: { de: 'Ausgelöst', en: 'Raised' },
  lifeAcked: { de: 'Bestätigt', en: 'Acknowledged' },
  lifeCleared: { de: 'Bedingung abgeklungen', en: 'Condition cleared' },
  lifeResolved: { de: 'Gelöst', en: 'Resolved' },
  lifePending: { de: 'Ausstehend', en: 'Pending' },
  lifeEscalated: {
    de: 'Eskaliert — kritisch und unbestätigt über die Frist hinaus.',
    en: 'Escalated — critical and unacknowledged past the deadline.',
  },
  clearedHint: {
    de: 'Die Bedingung ist abgeklungen, der Alarm wartet aber noch auf eine Lösung mit Notiz.',
    en: 'The condition has cleared, but the alarm still waits for a resolution with a note.',
  },
  asset: { de: 'Asset', en: 'Asset' },
  readOnly: {
    de: 'Nur Lesezugriff — Bestätigen und Lösen erfordern Schreibrecht in Maintenance.',
    en: 'Read-only — acknowledging and resolving need Maintenance write access.',
  },
  ackConfirmTitle: { de: 'Alarm bestätigen?', en: 'Acknowledge alarm?' },
  ackConfirmMessage: {
    de: 'Bestätigen stoppt die Eskalation und zeigt, dass sich jemand kümmert. Das lässt sich nicht zurücknehmen.',
    en: 'Acknowledging stops escalation and tells the team someone is on it. It cannot be undone.',
  },
  resolvePlaceholder: {
    de: 'Was war die Ursache, was wurde getan?',
    en: 'What caused it, and what was done?',
  },
  room: { de: 'Raum', en: 'Room' },
  sensor: { de: 'Sensor', en: 'Sensor' },
  // `sensor` carries a sensor for climate alarms, the service name for
  // service.offline and the tour code for cold-chain — "Source" is true for all.
  source: { de: 'Quelle', en: 'Source' },

  // Incident drill-down (reuses itsm data)
  colNumber: { de: 'Nummer', en: 'Number' },
  colTitle: { de: 'Titel', en: 'Title' },
  colStatus: { de: 'Status', en: 'Status' },
  colPriority: { de: 'Priorität', en: 'Priority' },
  colSeverity: { de: 'Schweregrad', en: 'Severity' },
  colMessage: { de: 'Meldung', en: 'Message' },
  colRaised: { de: 'Ausgelöst', en: 'Raised' },
  colType: { de: 'Typ', en: 'Type' },
  colWindow: { de: 'Zeitfenster', en: 'Window' },
  colRisk: { de: 'Risiko', en: 'Risk' },
  approver: { de: 'Genehmiger', en: 'Approver' },
  backoutPlan: { de: 'Backout-Plan', en: 'Backout plan' },
  justification: { de: 'Begründung', en: 'Justification' },
  description: { de: 'Beschreibung', en: 'Description' },
  major: { de: 'Major', en: 'Major' },

  // Room band strip
  roomsTitle: { de: 'Klimastatus je Raum', en: 'Room climate status' },
  roomReporting: { de: 'Meldet', en: 'Reporting' },
  roomBreach: { de: 'Ausbruch', en: 'Breach' },
  roomBreachChannels: { de: 'Ausbruch: {channels}', en: 'Breach: {channels}' },
  roomStale: { de: 'Veraltet', en: 'Stale' },
  roomLastReading: { de: 'Letzter Messwert {ago}', en: 'Last reading {ago}' },
  roomsSummaryReporting: { de: '{count} melden', en: '{count} reporting' },
  roomsSummaryStale: { de: '{count} veraltet', en: '{count} stale' },
  roomsSummaryBreach: { de: '{count} mit Ausbruch', en: '{count} in breach' },
  roomsLegend: {
    de: '„Meldet“ heißt: ein Messwert kam in den letzten 5 Minuten an. Es ist eine Frische-Prüfung, keine Bewertung gegen das Klimaband.',
    en: '“Reporting” means a reading arrived in the last 5 minutes. It is a freshness check, not an evaluation against the climate band.',
  },
  roomsUnknownSite: { de: 'Unbekannter Standort', en: 'Unknown site' },
  roomsError: { de: 'Der Raumstatus konnte nicht geladen werden.', en: 'Could not load the room status.' },
  roomsEmpty: {
    de: 'Noch kein Raum hat Telemetrie gesendet.',
    en: 'No room has sent telemetry yet.',
  },
  roomsEmptyHint: {
    de: 'Ein Raum erscheint hier, sobald sein Standort einen Messwert an die Cloud meldet — aktive Raumprogramme senden im 60s-Takt.',
    en: 'A room appears here once its site uplinks a reading to the cloud — active room programs report on a 60s cadence.',
  },

  // ── R-146 operations dashboard ──
  dashTitle: { de: 'Betriebs-Dashboard', en: 'Operations dashboard' },
  dashUnavailable: {
    de: 'Standortdaten derzeit nicht erreichbar — Werte werden als „—“ angezeigt.',
    en: 'Site data is unreachable right now — figures show as “—”.',
  },
  retrying: { de: 'Wird erneut versucht …', en: 'Retrying…' },
  calUnavailable: { de: 'Zeitplan nicht verfügbar.', en: 'Schedule unavailable.' },
  dashError: {
    de: 'Das Betriebs-Dashboard konnte nicht geladen werden.',
    en: 'Could not load the operations dashboard.',
  },
  slaWindow: { de: 'Letzte 30 Tage', en: 'Last 30 days' },
  prioTotal: { de: '{count} offen', en: '{count} open' },
  prioNone: { de: 'Keine offenen Störungen.', en: 'No open incidents.' },
  mttrAvg: { de: 'Ø {value} Std.', en: 'Avg {value} h' },
  mttrResolved: { de: '{count} gelöst', en: '{count} resolved' },
  mttrNoMonth: { de: 'nichts gelöst', en: 'nothing resolved' },
  assetsTotal: { de: '{count} Assets', en: '{count} assets' },
  calEmpty: {
    de: 'Keine Changes in den nächsten 7 Tagen geplant.',
    en: 'No changes scheduled in the next 7 days.',
  },
  calError: {
    de: 'Der Change-Kalender konnte nicht geladen werden.',
    en: 'Could not load the change calendar.',
  },
  calLegendEmergency: { de: 'Notfall', en: 'Emergency' },
  calLegendNormal: { de: 'Normal', en: 'Normal' },
  calLegendStandard: { de: 'Standard', en: 'Standard' },
  calLegendFrozen: { de: 'Freeze', en: 'Freeze' },
  calScheduled: { de: '{count} geplant', en: '{count} scheduled' },
  slaTitle: { de: 'SLA-Erfüllung', en: 'SLA attainment' },
  slaResponse: { de: 'Reaktion', en: 'Response' },
  slaResolution: { de: 'Lösung', en: 'Resolution' },
  slaMet: { de: '{met}/{total} erfüllt', en: '{met}/{total} met' },
  slaNoData: { de: 'Keine SLA-Daten', en: 'No SLA data' },
  slaTarget: { de: 'Ziel {target} %', en: 'Target {target}%' },
  stripFrozen: { de: 'Freeze-Fenster aktiv', en: 'Freeze window active' },
  prioTitle: { de: 'Offen nach Priorität', en: 'Open by priority' },
  mttrTitle: { de: 'MTTR-Trend (6 Monate)', en: 'MTTR trend (6 months)' },
  mttrUnit: { de: 'Std.', en: 'h' },
  mttrEmpty: {
    de: 'In den letzten 6 Monaten wurde keine Störung gelöst.',
    en: 'No incident was resolved in the last 6 months.',
  },
  assetsTitle: { de: 'Assets nach Status', en: 'Assets by state' },
  assetsEmpty: {
    de: 'Am Standort sind noch keine Assets erfasst.',
    en: 'No assets are registered at this site yet.',
  },
  myWorkTitle: { de: 'Meine Arbeit', en: 'My work' },
  myIncidents: { de: 'Störungen', en: 'Incidents' },
  myWorkOrders: { de: 'Arbeitsaufträge', en: 'Work orders' },
  myTransferOrders: { de: 'Transferaufträge', en: 'Transfer orders' },
  myWorkEmpty: { de: 'Nichts zugewiesen.', en: 'Nothing assigned.' },
  myWorkMore: { de: '+{count} weitere', en: '+{count} more' },
  calTitle: { de: 'Change-Kalender', en: 'Change calendar' },
  calDesc: {
    de: 'Geplante Change-Fenster und Freeze-Zeiträume.',
    en: 'Scheduled change windows and freeze periods.',
  },
  calOpen: { de: 'Kalender öffnen →', en: 'Open calendar →' },
} as const satisfies Namespace;
